import { useLayoutEffect } from 'react'

/** How many open phone chats hold the screen (a swap can briefly hold two). */
let held = 0
/** The page's own viewport meta, put back when the last chat closes. */
let pageViewport: string | null = null

/**
 * Phones: while a chat is open, it fills exactly the part of the screen the
 * person can see — header on top, composer on the bottom edge like the tab
 * bars, only the messages scroll. The visible part is the visual viewport, so
 * when the keyboard comes up the composer rides on top of it instead of being
 * scrolled away with the page. Sets html.has-phone-chat (phone.css pins the
 * chat with it) and --ph-vv-top / --ph-vv-h.
 *
 * It also caps the viewport at scale 1 while the chat is open: iOS zooms into
 * a field it finds small on focus and stays zoomed after the keyboard goes.
 * Safari still lets the person pinch-zoom (it ignores maximum-scale for that).
 */
export function usePinnedChatScreen(active: boolean) {
  useLayoutEffect(() => {
    if (!active) return
    const root = document.documentElement
    const vv = window.visualViewport
    const fit = () => {
      if (!vv) return
      // #root is zoomed by the interface scale: lengths inside it are divided back.
      root.style.setProperty('--ph-vv-top', `calc(${vv.offsetTop}px / var(--om-ui-scale, 1))`)
      root.style.setProperty('--ph-vv-h', `calc(${vv.height}px / var(--om-ui-scale, 1))`)
    }
    held += 1
    root.classList.add('has-phone-chat')
    const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
    if (meta && held === 1) {
      pageViewport = meta.content
      meta.content = `${pageViewport}, maximum-scale=1`
    }
    fit()
    vv?.addEventListener('resize', fit)
    vv?.addEventListener('scroll', fit)
    return () => {
      vv?.removeEventListener('resize', fit)
      vv?.removeEventListener('scroll', fit)
      held -= 1
      if (held === 0) {
        if (meta && pageViewport !== null) meta.content = pageViewport
        pageViewport = null
        root.classList.remove('has-phone-chat')
        root.style.removeProperty('--ph-vv-top')
        root.style.removeProperty('--ph-vv-h')
      }
    }
  }, [active])
}
